/* ============================================================================
   bcp-announcement-ticker.js — continuous "stock ticker" announcement bar.

   The section renders the offers ONCE into .announcement-bar__slides--ticker
   (it cannot render them twice: `content_for 'blocks'` is allowed only one
   occurrence per section). This script wraps that row in a track and appends
   exactly one clone of it, which is what makes the CSS loop seamless: the track
   animates to -50% of its own width, so the moment the original row scrolls out,
   the clone's first item is sitting precisely where the original started.
   One clone, no more — two would break the 50%.

   Duration is computed from the group's real pixel width divided by a px/sec
   rate, NOT hard-coded. A fixed duration would make the ticker accelerate every
   time someone adds an offer in the theme editor (same distance, more content,
   same seconds). Rate-based keeps reading speed constant forever.

   Vanilla + defer, no module graph: this must not depend on @theme/component,
   because the ticker replaces announcement-bar.js rather than extending it.
   ============================================================================ */
(function () {
  'use strict';

  var ROOT_SEL = '.announcement-bar__slider--ticker';
  var DEFAULT_RATE = 55; // px per second — medium-slow, comfortably readable

  function group(root) {
    return root.querySelector('[data-ticker-group]:not([data-ticker-clone])');
  }

  function build(root) {
    var g = group(root);
    if (!g) return;

    var track = root.querySelector('[data-ticker-track]');
    if (!track) {
      // Wrap the server-rendered row in a track we can animate as one unit.
      track = document.createElement('div');
      track.className = 'announcement-bar__ticker-track';
      track.setAttribute('data-ticker-track', '');
      g.parentNode.insertBefore(track, g);
      track.appendChild(g);
    }

    if (!track.querySelector('[data-ticker-clone]')) {
      var clone = g.cloneNode(true);
      clone.setAttribute('data-ticker-clone', '');
      // the copy is decorative duplication - keep it out of the a11y tree so a
      // screen reader announces each offer once, not twice
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    }

    measure(root);
  }

  function measure(root) {
    var g = group(root);
    if (!g) return;
    var rate = parseFloat(root.getAttribute('data-ticker-rate'));
    if (!isFinite(rate) || rate <= 0) rate = DEFAULT_RATE;

    var gap = parseFloat(getComputedStyle(root).getPropertyValue('--bcp-ticker-gap')) || 0;
    var distance = g.scrollWidth + gap; // one full group plus the gap before the clone
    if (!distance) return;

    root.style.setProperty('--bcp-ticker-speed', (distance / rate).toFixed(2) + 's');
  }

  function init(scope) {
    var roots = (scope || document).querySelectorAll(ROOT_SEL);
    for (var i = 0; i < roots.length; i++) {
      try { build(roots[i]); } catch (e) {}
    }
  }

  // Re-measure when the viewport changes (font scaling / rotation change widths).
  var t = null;
  window.addEventListener('resize', function () {
    clearTimeout(t);
    t = setTimeout(function () {
      var roots = document.querySelectorAll(ROOT_SEL);
      for (var i = 0; i < roots.length; i++) {
        try { measure(roots[i]); } catch (e) {}
      }
    }, 200);
  });

  // Web fonts land after first paint and change the row width — remeasure once ready.
  if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
    document.fonts.ready.then(function () { init(document); }).catch(function () {});
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  // The header is re-rendered by the Section Rendering API (and by the theme editor
  // on every settings change), which replaces the DOM and drops our clone.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:select', function (e) { init(e.target); });
})();
